import { FormEvent, useContext, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Checkbox, DefaultButton, Dialog, FontIcon, Stack, Text } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import { ThumbDislikeRegular, ThumbLikeRegular } from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import { AskResponse, Citation, Feedback, historyMessageFeedback } from '../../api'
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'

import { parseAnswer } from './AnswerParser'

import styles from './Answer.module.css'

interface Props {
  answer: AskResponse
  onCitationClicked: (citedDocument: Citation) => void
  onExectResultClicked: (answerId: string) => void
}

export const Answer = ({ answer, onCitationClicked, onExectResultClicked }: Props) => {

  const [logo, setLogo] = useState('')

  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined
    if (answer.feedback == undefined) return undefined
    if (answer.feedback.split(',').length > 1) return Feedback.Negative
    if (Object.values(Feedback).includes(answer.feedback)) return answer.feedback
    return Feedback.Neutral
  }

  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const splitMarkdownBySummary = (markdown: string) => {
    if (!markdown) {
      return { main: '', summary: '' }
    }

    // Split at a line-level Summary marker, including plain, markdown heading,
    // markdown bold (**Summary**) and raw HTML strong (<strong>Summary</strong>) forms.
    const summaryHeadingRegex =
      /^(\s{0,3}(?:#{1,6}\s*)?(?:(?:\*\*|__)\s*)?(?:<strong>\s*)?summary\b[^\n]*(?:\s*<\/strong>)?(?:\s*(?:\*\*|__))?\s*)$/im
    const match = summaryHeadingRegex.exec(markdown)

    if (!match || match.index < 0) {
      return { main: markdown, summary: '' }
    }

    return {
      main: markdown.slice(0, match.index).trim(),
      summary: markdown.slice(match.index).trim()
    }
  }
  const markdownSections = useMemo(
    () => splitMarkdownBySummary(parsedAnswer?.markdownFormatText ?? ''),
    [parsedAnswer?.markdownFormatText]
  )
  const summarySectionContent = markdownSections.summary
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [showReportInappropriateFeedback, setShowReportInappropriateFeedback] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<Feedback[]>([])
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED =
    appStateContext?.state.frontendSettings?.feedback_enabled && appStateContext?.state.isCosmosDBAvailable?.cosmosDB
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer

  const ui = appStateContext?.state.frontendSettings?.ui
  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
    let citationFilename = ''

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id) + 1 : '')
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`
    } else {
      citationFilename = `Citation ${index}`
    }

    if((citation.title && citation.title.includes("#")) || (citation.filepath && citation.filepath.includes("#"))) {
      let url = ''
      url = citation.title?citation.title:citation.filepath?citation.filepath:citationFilename
      url = url.replaceAll("U+3F", "?")
      url = url.replaceAll("U+3A", ":")
      url = url.replaceAll("U+2A", "*")
      url = url.replaceAll("U+22", "\"")
      url = url.replaceAll("U+3C", "<")
      url = url.replaceAll("U+3E", ">")
      url = url.replaceAll("U+7C", "|")
      url = url.replaceAll("#", "/")
      url = url.replaceAll(".html", "")
      url = "https://" + url
      citationFilename = url
    }

    return citationFilename
  }

  const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == Feedback.Positive) {
      newFeedbackState = Feedback.Neutral
    } else {
      newFeedbackState = Feedback.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
    setFeedbackState(newFeedbackState)

    // Update message feedback in db
    await historyMessageFeedback(answer.message_id, newFeedbackState, '')
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (feedbackState === undefined || feedbackState === Feedback.Neutral || feedbackState === Feedback.Positive) {
      newFeedbackState = Feedback.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = Feedback.Neutral
      setFeedbackState(newFeedbackState)
      await historyMessageFeedback(answer.message_id, Feedback.Neutral, '')
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState }
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as Feedback

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined) return
    await historyMessageFeedback(answer.message_id, negativeFeedbackList.join(','), otherText)
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setShowReportInappropriateFeedback(false)
    setNegativeFeedbackList([])
  }

  const UnhelpfulFeedbackContent = () => {
    return (
      <>
        <div>Why wasn't this response helpful?</div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="The response isn't helpful."
            id={Feedback.WrongCitation}
            defaultChecked={negativeFeedbackList.includes(Feedback.WrongCitation)}
            onChange={updateFeedbackList}></Checkbox>          
          <Checkbox
            label="The response isn't accurate."
            id={Feedback.InaccurateOrIrrelevant}
            defaultChecked={negativeFeedbackList.includes(Feedback.InaccurateOrIrrelevant)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="There was an issue with the supporting references."
            id={Feedback.OutOfScope}
            defaultChecked={negativeFeedbackList.includes(Feedback.OutOfScope)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherUnhelpful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherUnhelpful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
        <div onClick={() => setShowReportInappropriateFeedback(true)} style={{ color: '#115EA3', cursor: 'pointer' }}>
          Report inappropriate content
        </div>
      </>
    )
  }

  const [otherText, setOtherText] = useState<string>('')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtherText(event.target.value);
  };

  const ReportInappropriateFeedbackContent = () => {
    return (
      <>
        <div>
          The content is <span style={{ color: 'red' }}>*</span>
        </div>
        <Stack tokens={{ childrenGap: 4 }}>
          <Checkbox
            label="Hate speech, stereotyping, demeaning"
            id={Feedback.HateSpeech}
            defaultChecked={negativeFeedbackList.includes(Feedback.HateSpeech)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Violent: glorification of violence, self-harm"
            id={Feedback.Violent}
            defaultChecked={negativeFeedbackList.includes(Feedback.Violent)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Sexual: explicit content, grooming"
            id={Feedback.Sexual}
            defaultChecked={negativeFeedbackList.includes(Feedback.Sexual)}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Manipulative: devious, emotional, pushy, bullying"
            defaultChecked={negativeFeedbackList.includes(Feedback.Manipulative)}
            id={Feedback.Manipulative}
            onChange={updateFeedbackList}></Checkbox>
          <Checkbox
            label="Other"
            id={Feedback.OtherHarmful}
            defaultChecked={negativeFeedbackList.includes(Feedback.OtherHarmful)}
            onChange={updateFeedbackList}></Checkbox>
        </Stack>
      </>
    )
  }

  const components = {
    code({ node, ...props }: { node: any;[key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    }
  }
  return (
    <>
      <Stack className={styles.answerContainer} tabIndex={0}>
        {!!markdownSections.main && (
          <Stack.Item>
            <Stack horizontal grow>
              <Stack.Item grow>
                <ReactMarkdown
                  linkTarget="_blank"
                  remarkPlugins={[remarkGfm, supersub]}
                  children={
                    SANITIZE_ANSWER
                      ? DOMPurify.sanitize(markdownSections.main, {
                        ALLOWED_TAGS: XSSAllowTags,
                        ALLOWED_ATTR: XSSAllowAttributes
                      })
                      : markdownSections.main
                  }
                  className={styles.answerText}
                  components={components}
                />
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}
        {parsedAnswer?.generated_chart !== null && (
          <Stack className={styles.answerContainer}>
            <Stack.Item grow>
              <img src={`data:image/png;base64, ${parsedAnswer?.generated_chart}`} />
            </Stack.Item>
          </Stack>
        )}
        {(!!summarySectionContent || (parsedAnswer?.citations?.length ?? 0) > 0 || ui?.chat_response_contactmessage) && (
          <div className={styles.summaryResourcesBox}>
            {!!summarySectionContent && (
              <div className={styles.summarySection}>
                <ReactMarkdown
                  linkTarget="_blank"
                  remarkPlugins={[remarkGfm, supersub]}
                  children={
                    SANITIZE_ANSWER
                      ? DOMPurify.sanitize(summarySectionContent, {
                        ALLOWED_TAGS: XSSAllowTags,
                        ALLOWED_ATTR: XSSAllowAttributes
                      })
                      : summarySectionContent
                  }
                  className={styles.answerText}
                  components={components}
                />
              </div>
            )}
            <div className={styles.resourcesBox}>
              <h4 className={styles.resourcesHeader}>Resources</h4>
              {ui?.chat_response_contactmessage && (
                <p className={styles.resourcesDisclaimer}>
                  {(ui.chat_response_contactmessage || '').split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                    /^https?:\/\//.test(part) ? (
                      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
                        {part}
                      </a>
                    ) : (
                      part
                    )
                  )}
                </p>
              )}
              {(parsedAnswer?.citations?.length ?? 0) > 0 && (
                <div className={styles.resourcesLinks}>
                  {parsedAnswer!.citations.map((citation, idx) => {
                    const displayUrl =
                      citation.url && !citation.url.includes('blob.core')
                        ? citation.url
                        : createCitationFilepath(citation, idx + 1)
                    const isLink =
                      (citation.title != null && citation.title.includes('#')) ||
                      (citation.filepath != null && citation.filepath.includes('#')) ||
                      (citation.url != null && !citation.url.includes('blob.core'))
                    if (isLink) {
                      return (
                        <a
                          href={displayUrl}
                          key={idx}
                          tabIndex={0}
                          role="link"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.resourceLink}
                          aria-label={displayUrl}>
                          <div className={styles.resourceLinkNum}>{idx + 1}</div>
                          <span className={styles.resourceLinkText}>{displayUrl}</span>
                        </a>
                      )
                    } else {
                      return (
                        <span
                          key={idx}
                          tabIndex={0}
                          role="link"
                          onClick={() => onCitationClicked(citation)}
                          onKeyDown={e =>
                            e.key === 'Enter' || e.key === ' ' ? onCitationClicked(citation) : null
                          }
                          className={styles.resourceLink}
                          aria-label={createCitationFilepath(citation, idx + 1, true)}>
                          <div className={styles.resourceLinkNum}>{idx + 1}</div>
                          <span className={styles.resourceLinkText}>
                            {createCitationFilepath(citation, idx + 1, true)}
                          </span>
                        </span>
                      )
                    }
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feedback icons */}
        <div className={styles.feedbackRow}>
          {FEEDBACK_ENABLED && answer.message_id !== undefined && (
            <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 8 }}>
              <ThumbLikeRegular
                aria-hidden="false"
                aria-label="Like this response"
                onClick={() => onLikeResponseClicked()}
                style={
                  feedbackState === Feedback.Positive ||
                  appStateContext?.state.feedbackState[answer.message_id] === Feedback.Positive
                    ? { color: 'darkgreen', cursor: 'pointer', fontSize: '20px' }
                    : { color: 'slategray', cursor: 'pointer', fontSize: '20px' }
                }
              />
              <ThumbDislikeRegular
                aria-hidden="false"
                aria-label="Dislike this response"
                onClick={() => onDislikeResponseClicked()}
                style={
                  feedbackState !== Feedback.Positive &&
                  feedbackState !== Feedback.Neutral &&
                  feedbackState !== undefined
                    ? { color: 'darkred', cursor: 'pointer', fontSize: '20px' }
                    : { color: 'slategray', cursor: 'pointer', fontSize: '20px' }
                }
              />
            </Stack>
          )}
          {!!answer.exec_results?.length && (
            <Stack horizontal horizontalAlign="start" verticalAlign="center">
              <Text
                className={styles.accordionTitle}
                onClick={() => onExectResultClicked(answer.message_id ?? '')}
                aria-label="Open Intents"
                tabIndex={0}
                role="button">
                <span>Show Intents</span>
              </Text>
              <FontIcon
                className={styles.accordionIcon}
                onClick={handleChevronClick}
                iconName={'ChevronRight'}
              />
            </Stack>
          )}
        </div>
      </Stack>
      <Dialog
        onDismiss={() => {
          resetFeedbackDialog()
          setFeedbackState(Feedback.Neutral)
        }}
        hidden={!isFeedbackDialogOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '600px',
                  minHeight: '100px'
                }
              }
            }
          ]
        }}
        dialogContentProps={{
          title: 'Submit Feedback',
          showCloseButton: true
        }}>
        <Stack tokens={{ childrenGap: 4 }}>
          <div>Your feedback will improve this experience.</div>

          {!showReportInappropriateFeedback ? <UnhelpfulFeedbackContent /> : <ReportInappropriateFeedbackContent />}
          <div> <input type="text" id="otherTextid" value={otherText} onChange={handleChange} /> </div>
          <div>By pressing submit, your feedback will be visible to the application owner.</div>

          <DefaultButton disabled={negativeFeedbackList.length < 1} onClick={onSubmitNegativeFeedback}>
            Submit
          </DefaultButton>
        </Stack>
      </Dialog>
    </>
  )
}
